import { RouteComponentProps } from 'react-router';
import { observer } from 'mobx-react-lite';
import { useDependencies, provide } from '@servicetitan/react-ioc';
import React, { useEffect, useState } from 'react';
import { Button, Headline, BodyText, Link } from '@servicetitan/design-system';

import {
    ServiceFinanceStore,
    ApplicationStageMobile,
    ApplicationStatus,
} from '../../../stores/service-finance.store';
import { FinancingAPIService } from 'src/api/api/financing-bridgeAPI.service';
import { LoansAPIService } from 'src/api/api/loansAPI.service';
import { PlanSelectionPage } from './select-plan/plan-selection-page';
import { FinancingLoader } from '../desktop/financing-loader';
import { EstimateStore } from '../../../stores/estimate.store';
import { EstimatesApiService, Estimate } from 'src/api';
import { ClientDataStore } from 'src/core/stores/client-data.store';
import { ClientDataApiService, LoginApiService } from 'src/api';
import { RouterStore } from 'src/core/stores/router.store';

import classnames from 'classnames';
import styles from './application-container.less';
import { AddInfoPage } from './application/add-info-page';
import { AddCoApplicantInfoPage } from './application/add-coapplicant-info-page';
import { ReviewPage } from './review/review-page';
import { CoReviewPage } from './review/co-review-page';
import { ReviewResponse } from './response/review-response';

interface RouteParams {
    bundleId: string;
    estimateId: string;
}

interface ComponentData {
    StepTitle?: string | undefined;
    Component: React.FC;
}

const stageToComponentMap = new Map<ApplicationStageMobile, ComponentData>([
    [
        ApplicationStageMobile.SelectPlan,
        {
            StepTitle: 'Step 1 - Select a Plan',
            Component: PlanSelectionPage,
        },
    ],
    [
        ApplicationStageMobile.AddInfo,
        {
            StepTitle: 'Step 2 - Add Applicant Info',
            Component: AddInfoPage,
        },
    ],
    [
        ApplicationStageMobile.AddCoApplicantInfo,
        {
            StepTitle: 'Step 2 - Add Co-Applicant Info',
            Component: AddCoApplicantInfoPage,
        },
    ],
    [
        ApplicationStageMobile.ReviewApplication,
        {
            StepTitle: 'Step 3 - Review',
            Component: ReviewPage,
        },
    ],
    [
        ApplicationStageMobile.PrimaryApplicantReviewApplication,
        {
            StepTitle: 'Step 3 - Review',
            Component: ReviewPage,
        },
    ],
    [
        ApplicationStageMobile.CoApplicantReviewApplication,
        {
            StepTitle: 'Step 3 - Review',
            Component: CoReviewPage,
        },
    ],
    [
        ApplicationStageMobile.ReviewResponse,
        {
            Component: ReviewResponse,
        },
    ],
]);

export const ApplicationContainer: React.FC<RouteComponentProps<RouteParams>> = provide({
    singletons: [
        ServiceFinanceStore,
        FinancingAPIService,
        LoansAPIService,
        EstimateStore,
        EstimatesApiService,
        ClientDataStore,
        ClientDataApiService,
        LoginApiService,
        RouterStore,
    ],
})(
    observer(
        ({
            match: {
                params: { bundleId, estimateId },
            },
            history,
        }) => {
            const [initialized, setInitialized] = useState(false);
            const [store] = useDependencies(ServiceFinanceStore);
            const [estimateStore] = useDependencies(EstimateStore);

            const CurrentPage = stageToComponentMap.get(store.currentStageMobile);

            if (CurrentPage && store.currentStageMobile === ApplicationStageMobile.ReviewResponse) {
                switch (store.applicationStatus!) {
                    case ApplicationStatus.Approved:
                        CurrentPage['StepTitle'] = 'Approve';
                        break;
                    case ApplicationStatus.CounterOffer:
                        CurrentPage['StepTitle'] = 'Counter Offer';
                        break;
                    case ApplicationStatus.UnderReview:
                        CurrentPage['StepTitle'] = 'Under Review';
                        break;
                    default:
                        CurrentPage['StepTitle'] = 'Declined';
                        break;
                }
            }

            const renderLink = () => {
                return CurrentPage &&
                    store.currentStageMobile === ApplicationStageMobile.ReviewResponse ? (
                    <Link
                        href={`/estimate/${bundleId}/${estimateId}`}
                        className={styles.estimateLink}
                        color="primary"
                    >
                        Back to Estimates
                    </Link>
                ) : null;
            };

            const buttonsData = store.getButtonsDataMobile();

            const initializeStore = async () => {
                const bundle = await estimateStore.getEstimateBundle(bundleId);
                const estimates = bundle.estimates;
                let estimate: Estimate | undefined = undefined;
                if (estimates.length > 0) {
                    estimate = estimates.find((e) => e.id === estimateId);
                }
                const backAction = () => {
                    history.replace(`/estimate/${bundleId}/${estimateId}`);
                };
                await store.initialize(() => {}, estimate, backAction);
            };

            useEffect(() => {
                async function initializeAll() {
                    await initializeStore();
                    await store.getSelectedProducts();
                    setInitialized(true);
                }
                initializeAll();
            }, []);

            return !store.isProcessing && initialized ? (
                <React.Fragment>
                    <div className="page">
                        <div className={styles.title}>
                            <BodyText subdued size="small" className="m-b-half">
                                Apply for Financing
                            </BodyText>
                            {renderLink()}
                            {CurrentPage && (
                                <Headline size="small" className="m-b-0">
                                    {CurrentPage.StepTitle}
                                </Headline>
                            )}
                        </div>
                        {CurrentPage && <CurrentPage.Component />}
                    </div>
                    <div className="sticky-button-wrapper">
                        {buttonsData.backAction && (
                            <Button
                                variant="contained"
                                className={styles.button}
                                onClick={buttonsData.backAction}
                            >
                                Back
                            </Button>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className={classnames('next-step-button', {
                                [styles.fullWidth]: !buttonsData.backAction,
                                [styles.button]: !store.addingApplicantInfoMobile,
                                [styles.buttonDisableHover]: store.addingApplicantInfoMobile,
                            })}
                            disabled={store.isProcessing || !store.isNextButtonEnabledMobile}
                            onClick={buttonsData.action}
                        >
                            {buttonsData.nextTitle}
                        </Button>
                    </div>
                </React.Fragment>
            ) : (
                <div className="page">
                    <div className={styles.loader}>
                        <FinancingLoader />
                    </div>
                </div>
            );
        }
    )
);
